import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns'

export type DashboardPeriodPreset = 'today' | 'last7' | 'month' | 'lastMonth' | 'custom'

export type DashboardPeriod = {
  preset: DashboardPeriodPreset
  from: Date
  to: Date
}

export const DASHBOARD_PRESET_LABELS: Record<DashboardPeriodPreset, string> = {
  today: 'Hoje',
  last7: '7 dias',
  month: 'Este mês',
  lastMonth: 'Mês passado',
  custom: 'Personalizado',
}

export const DASHBOARD_SELECTABLE_PRESETS: DashboardPeriodPreset[] = [
  'today',
  'last7',
  'month',
  'lastMonth',
]

/**
 * Month-to-date is the default because the rhythm of a company is monthly: targets,
 * commissions and closing are all monthly, so "how are we doing?" almost always means
 * "how are we doing this month?".
 */
export function resolvePeriod(preset: Exclude<DashboardPeriodPreset, 'custom'>, now = new Date()): DashboardPeriod {
  switch (preset) {
    case 'today':
      return { preset, from: startOfDay(now), to: endOfDay(now) }
    case 'last7':
      return { preset, from: startOfDay(subDays(now, 6)), to: endOfDay(now) }
    case 'lastMonth': {
      const reference = subMonths(now, 1)
      return { preset, from: startOfMonth(reference), to: endOfMonth(reference) }
    }
    case 'month':
    default:
      return { preset: 'month', from: startOfMonth(now), to: endOfDay(now) }
  }
}

export function defaultDashboardPeriod(now = new Date()): DashboardPeriod {
  return resolvePeriod('month', now)
}

export type DashboardComparison = {
  from: Date
  to: Date
  label: string
}

/**
 * Each preset defines its own comparison window, because a single rule produces
 * misleading numbers. Comparing month-to-date against the equal-length window right
 * before it would pit the beginning of one month against the end of the previous one,
 * and sales typically cluster at month end - the drop would be pure calendar effect.
 */
export function resolveComparison(period: DashboardPeriod): DashboardComparison {
  const range = ((): { from: Date; to: Date } => {
    switch (period.preset) {
      case 'today': {
        const yesterday = subDays(period.from, 1)
        return { from: startOfDay(yesterday), to: endOfDay(yesterday) }
      }
      case 'month': {
        // Same days of the previous month. subMonths clamps overflow, so the 31st of a
        // month maps to the 30th (or 28th) of a shorter one instead of spilling over.
        const previousMonth = subMonths(period.from, 1)
        return {
          from: startOfMonth(previousMonth),
          to: endOfDay(subMonths(period.to, 1)),
        }
      }
      case 'lastMonth': {
        const previousMonth = subMonths(period.from, 1)
        return { from: startOfMonth(previousMonth), to: endOfMonth(previousMonth) }
      }
      case 'last7':
      case 'custom':
      default: {
        const days = differenceInCalendarDays(period.to, period.from) + 1
        return {
          from: startOfDay(subDays(period.from, days)),
          to: endOfDay(subDays(period.to, days)),
        }
      }
    }
  })()

  return {
    ...range,
    label:
      period.preset === 'today'
        ? 'vs. ontem'
        : `vs. ${format(range.from, 'dd/MM')} a ${format(range.to, 'dd/MM')}`,
  }
}

/** Inclusive start / exclusive end, matching the convention used by the reports hooks. */
export function toIsoBounds(from: Date, to: Date) {
  return {
    fromIso: startOfDay(from).toISOString(),
    toIso: new Date(endOfDay(to).getTime() + 1).toISOString(),
  }
}

export function formatPeriodLabel(period: DashboardPeriod): string {
  if (period.preset === 'today') return 'Hoje'
  return `${format(period.from, 'dd/MM/yyyy')} a ${format(period.to, 'dd/MM/yyyy')}`
}
